import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, update } from "firebase/database";
import "../styles/BeersView.css";
import { getAuth } from "firebase/auth";

const adminEmails = ["stevenvivino@gmail.com", "jvivino@gmail.com", "brookekirchenwitz@gmail.com"];

const BeersView = () => {
  const [beers, setBeers] = useState([]);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  // This will store any brewer descriptions we load for each beer
  // Keyed by beer slug, then we pick the "selected" one if found.
  const [brewersDescriptions, setBrewersDescriptions] = useState({});

  const auth = getAuth();

  useEffect(() => {
    const user = auth.currentUser;
    if (user && user.email) {
      setUserEmail(user.email);
    }

    const db = getDatabase();
    const beersRef = ref(db, "beers");
    const ratingsRef = ref(db, "ratings");
    // We'll also read from "brewers_descriptions"
    const brewersDescRef = ref(db, "brewers_descriptions");

    const unsubscribeBeers = onValue(beersRef, (snapshot) => {
      const beersData = snapshot.val();
      if (beersData) {
        const transformedBeers = Object.keys(beersData).map((key) => ({
          ...beersData[key],
          id: convertNameToSlug(beersData[key].beer_name),
        }));
        setBeers(transformedBeers);
      }
    });

    const unsubscribeRatings = onValue(ratingsRef, (snapshot) => {
      const ratingsData = snapshot.val();
      if (ratingsData) {
        setRatings(calculateAverageRatings(ratingsData));
      } else {
        setRatings({});
      }
      setLoading(false);
    });

    // 3) Load brewer's descriptions
    const unsubscribeBrewersDesc = onValue(brewersDescRef, (snapshot) => {
      const descData = snapshot.val() || {};
      setBrewersDescriptions(descData);
    });

    return () => {
      unsubscribeBeers();
      unsubscribeRatings();
      unsubscribeBrewersDesc();
    };
  }, [auth.currentUser]);

  function calculateAverageRatings(ratingsData) {
    let averages = {};
    for (const beer in ratingsData) {
      const beerRatings = Object.values(ratingsData[beer]).map(
        (emailData) => emailData.rating
      );
      const average = beerRatings.length
        ? (beerRatings.reduce((a, b) => a + b, 0) / beerRatings.length).toFixed(2)
        : "No ratings";
      averages[beer] = average;
    }
    return averages;
  }

  function convertNameToSlug(input) {
    if (typeof input !== "string") return null;

    // 1) Remove diacritics (similar to "Any-Latin; Latin-ASCII; Lower;")
    //    We'll normalize() to NFD and remove combining marks, then lowercase.
    let str = input
      .normalize("NFD")                           // separate accent from base char
      .replace(/[\u0300-\u036f]/g, "")            // remove combining marks
      .toLowerCase();

    // 2) Split on anything *not* [0-9a-z-], effectively "inverting" our safe characters
    //    This is like the Swift approach of using `String.slugSafeCharacters.inverted`
    //    except we do it via a regex that matches invalid chars.
    const segments = str.split(/[^0-9a-z-]+/);

    // 3) Filter out any empty elements and join with '-'
    const filtered = segments.filter(Boolean);
    const result = filtered.join("-");

    // 4) Return null if empty, else the slug
    return result.length > 0 ? result : null;
  }
  function formatDateKey(date) {
    // Get the date part (e.g. "Nov 29, 2023")
    const datePart = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });

    // Get the time part (e.g. "11:38 PM")
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });

    // Combine them with " at "
    return `${datePart} at ${timePart}`;
  }

  // Submit brewer's description to "brewers_descriptions/{beerSlug}/{dateKey}/..."
  const submitBrewerDescription = (beerSlug, descText, setDescText) => {
    const db = getDatabase();
    const beerRef = ref(db, `brewers_descriptions/${beerSlug}`);
    const dateKey = formatDateKey(new Date()); // e.g. "Nov 29, 2023, 11:38 PM"

    // 1) Fetch existing descriptions for this beer to unset `isSelected`
    onValue(
      beerRef,
      (snapshot) => {
        const existingData = snapshot.val() || {};
        // We'll collect all needed updates in one object
        // e.g. { "brewers_descriptions/slug/dateKey/isSelected": false, ... }
        let updates = {};

        // For each dateKey => { description, isSelected } => set isSelected = false
        Object.keys(existingData).forEach((oldDateKey) => {
          // If oldDateKey is the same as the new dateKey, skip
          if (oldDateKey === dateKey) return;
          updates[`brewers_descriptions/${beerSlug}/${oldDateKey}/isSelected`] = false;
        });

        // 2) Add new description with isSelected = true
        updates[`brewers_descriptions/${beerSlug}/${dateKey}`] = {
          description: descText,
          isSelected: true,
        };

        // 3) Perform a single multi-path update
        update(ref(db), updates)
          .then(() => {
            console.log("Description updated!");
            // Show a temporary success message
            setSuccessMessage("Brewer's description updated successfully!");
            // Clear it after 3 seconds
            setTimeout(() => setSuccessMessage(""), 3000);
          })
          .catch((err) => {
            console.error("Error updating description:", err);
          });
      },
      {
        // Only run once for this read
        onlyOnce: true,
      }
    );
  };

  if (loading) {
    return <div>Loading beers...</div>;
  }

  const isAdmin = adminEmails.includes(userEmail);

  return (
    <div className="beers-list">
      {/* If successMessage is non-empty, show a small popover */}
      {successMessage && (
        <div className="success-popup">
          {successMessage}
        </div>
      )}

      {beers.map((beer) => (
        <BeerItem
          key={beer.id}
          beer={{
            ...beer,
            averageRating: ratings[beer.id] || "No ratings",
          }}
          isAdmin={isAdmin}
          onSubmitDescription={submitBrewerDescription}
          brewersDescriptions={brewersDescriptions[beer.id] || {}} // Pass only this beer's desc data
        />
      ))}
    </div>
  );
};

function BeerItem({ beer, isAdmin, onSubmitDescription, brewersDescriptions }) {
  // The local text area state
  const [descText, setDescText] = useState("");

  // Check if there's an existing "selected" description
  let existingDescription = "";
  if (brewersDescriptions) {
    const descEntries = Object.values(brewersDescriptions);
    const selectedEntry = descEntries.find((entry) => entry.isSelected === true);
    if (selectedEntry) {
      existingDescription = selectedEntry.description;
    }
  }

  // If we find an existingDescription, load it into descText initially
  useEffect(() => {
    if (existingDescription) {
      setDescText(existingDescription);
    }
  }, [existingDescription]);

  return (
    <div className="beer-item">
      <img src={beer.beer_image} alt={beer.beer_name} />
      <h3>{beer.beer_name}</h3>
      <p>
        <b>Brewery: </b>
        {beer.beer_brewery}
      </p>
      <p>
        <b>Style: </b>
        {beer.beer_style}
      </p>
      <p>
        <b>Bottle Size: </b>
        {beer.beer_bottle_size}
      </p>
      <p>
        <b>Abv: </b>
        {beer.beer_abv}
      </p>
      <p>
        <b>CRAFT Rating: </b>
        {beer.averageRating}
      </p>

      {/* If there's an existing brewer's description, display it. The text area is also populated. */}
      {existingDescription && (
        <div className="existing-description">
          <p>
            <b>Brewer's Description:</b> {existingDescription}
          </p>
        </div>
      )}

      {/* Admin can always edit / submit new or updated text */}
      {isAdmin && (
        <div className="description-editor">
          <textarea
            style={{ width: "100%", minHeight: "150px" }}
            value={descText}
            onChange={(e) => setDescText(e.target.value)}
            placeholder="Enter or update the brewer's description..."
          />
          <button onClick={() => onSubmitDescription(beer.id, descText, setDescText)}>
            Submit Description
          </button>
        </div>
      )}
    </div>
  );
}

export default BeersView;
